<template>
  <div class="faq-panel">
    <div class="faq-header">
      <i class="fas fa-question-circle"></i>
      <h2>Frequently Asked Questions</h2>
      <p class="faq-subtitle">Everything you need to know about {{ courseName }}</p>
    </div>

    <div class="faq-container">
      <div
        v-for="(faq, index) in faqs"
        :key="index"
        class="faq-item"
        :class="{ active: activeFAQ === index }"
      >
        <button
          class="faq-question"
          @click="toggleFAQ(index)"
          :aria-expanded="activeFAQ === index"
        >
          <span class="question-text">{{ faq.question }}</span>
          <i class="fas" :class="activeFAQ === index ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
        </button>
        <transition name="faq-slide">
          <div v-show="activeFAQ === index" class="faq-answer">
            <div class="answer-content">
              <p v-html="faq.answer"></p>
            </div>
          </div>
        </transition>
      </div>
    </div>

    <div class="faq-footer">
      <div class="still-have-questions">
        <i class="fas fa-headset"></i>
        <div>
          <h3>Still have questions?</h3>
          <p>Contact our support team for personalized assistance</p>
        </div>
        <a href="mailto:support@bheem.academy" class="contact-btn">
          <i class="fas fa-envelope"></i>
          Contact Support
        </a>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'

interface FAQ {
  question: string
  answer: string
}

const props = defineProps<{
  courseName: string
  courseId: number
  faqs?: FAQ[] | null
}>()

const activeFAQ = ref<number | null>(null)

const toggleFAQ = (index: number) => {
  activeFAQ.value = activeFAQ.value === index ? null : index
}

// Use provided FAQs or generate default FAQs
const faqs = computed<FAQ[]>(() => {
  // If FAQs are provided from database, use them
  if (props.faqs && Array.isArray(props.faqs) && props.faqs.length > 0) {
    return props.faqs
  }

  // Otherwise, generate default FAQs
  const courseName = props.courseName.toLowerCase()

  // Base FAQs for all courses
  const baseFAQs: FAQ[] = [
    {
      question: `What will I learn in the ${props.courseName} course?`,
      answer: `This course covers comprehensive AI and technology skills tailored for your level. You'll learn practical applications, hands-on projects, and industry-relevant tools that will prepare you for real-world challenges.`
    },
    {
      question: 'How long does the course take to complete?',
      answer: 'The course duration varies based on your pace. Typically, students complete it in 3-6 months with dedicated study. You have lifetime access to all course materials, so you can learn at your own speed.'
    },
    {
      question: 'Do I need any prior experience?',
      answer: 'No prior experience is required! Our courses are designed to accommodate beginners while also challenging advanced learners. We start with fundamentals and progressively build your skills.'
    },
    {
      question: 'Will I receive a certificate upon completion?',
      answer: 'Yes! Upon successfully completing the course, you\'ll receive an <strong>industry-recognized certificate</strong> that you can add to your resume and LinkedIn profile. This certification demonstrates your expertise to potential employers.'
    },
    {
      question: 'What support is available during the course?',
      answer: 'You\'ll have access to 24/7 support through our dedicated help desk, live Q&A sessions with instructors, a vibrant community forum, and personalized mentorship. We\'re here to ensure your success!'
    }
  ]

  // Add course-specific FAQs
  const specificFAQs: FAQ[] = []

  // AI/ChatGPT related courses
  if (courseName.includes('chatgpt') || courseName.includes('gpt') || courseName.includes('prompt')) {
    specificFAQs.push({
      question: 'What AI tools will I learn to use?',
      answer: 'You\'ll master ChatGPT, GPT-4, Claude, Midjourney, DALL-E, and other cutting-edge AI tools. We focus on practical applications including content creation, automation, and business solutions.'
    })
    specificFAQs.push({
      question: 'Can I use these skills for my business or freelancing?',
      answer: 'Absolutely! Many of our students use these AI skills to start freelancing, enhance their business operations, or even launch AI consulting services. We provide templates and real-world case studies.'
    })
  }

  // Python related courses
  if (courseName.includes('python')) {
    specificFAQs.push({
      question: 'Do I need to install any software?',
      answer: 'We\'ll guide you through installing Python, VS Code, and other necessary tools. All software we use is free and open-source. We also provide cloud-based alternatives if you prefer not to install anything.'
    })
    specificFAQs.push({
      question: 'What projects will I build?',
      answer: 'You\'ll create real-world projects like AI chatbots, data analysis tools, web scrapers, automation scripts, and machine learning applications. Each project is portfolio-ready!'
    })
  }

  // Kids/Junior courses
  if (courseName.includes('junior') || courseName.includes('kids') || courseName.includes('bheem junior')) {
    specificFAQs.push({
      question: 'Is this course suitable for my child\'s age?',
      answer: 'This course is designed for ages 8-12 with age-appropriate content, gamified learning, and parental controls. Our interactive approach keeps young learners engaged and excited about technology.'
    })
    specificFAQs.push({
      question: 'Will my child need parental supervision?',
      answer: 'While the platform is safe and child-friendly, we recommend parental guidance for younger children (8-10 years). Older children (11-12 years) can usually learn independently with periodic check-ins.'
    })
  }

  // Youth courses
  if (courseName.includes('youth') || courseName.includes('teen')) {
    specificFAQs.push({
      question: 'How will this help with my future career?',
      answer: 'AI and technology skills are in high demand across all industries. This course prepares you for university programs, internships, and entry-level tech positions. You\'ll build a portfolio that impresses colleges and employers.'
    })
    specificFAQs.push({
      question: 'Can I work on this alongside school?',
      answer: 'Yes! The course is designed to fit around your school schedule. You can study in the evenings or weekends, and pause whenever needed. Most students dedicate 3-5 hours per week.'
    })
  }

  // Professional/Advanced courses
  if (courseName.includes('professional') || courseName.includes('advanced') || courseName.includes('superstar')) {
    specificFAQs.push({
      question: 'What career opportunities will this unlock?',
      answer: 'This course prepares you for roles like AI Specialist, Machine Learning Engineer, Data Scientist, Automation Expert, and AI Consultant. Our alumni work at top tech companies and successful startups.'
    })
    specificFAQs.push({
      question: 'Is job placement assistance included?',
      answer: 'Yes! We offer resume reviews, interview preparation, portfolio building guidance, and connections to our hiring partner network. Our career support continues even after course completion.'
    })
  }

  // Machine Learning courses
  if (courseName.includes('machine learning') || courseName.includes('ml')) {
    specificFAQs.push({
      question: 'Do I need strong math skills?',
      answer: 'Basic math understanding is helpful, but we explain all concepts from scratch. We focus on practical application rather than complex theory. If you can do high school math, you\'ll be fine!'
    })
  }

  // Data Science courses
  if (courseName.includes('data science') || courseName.includes('data')) {
    specificFAQs.push({
      question: 'What tools and libraries will I learn?',
      answer: 'You\'ll master Pandas, NumPy, Matplotlib, Scikit-learn, TensorFlow, and more. We also cover SQL, data visualization, and statistical analysis tools used by professional data scientists.'
    })
  }

  // Combine base FAQs with specific FAQs
  return [...specificFAQs, ...baseFAQs]
})
</script>

<style scoped>
.faq-panel {
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
}

.faq-header {
  text-align: center;
  margin-bottom: 50px;
}

.faq-header i {
  font-size: 60px;
  background: linear-gradient(135deg, #3b82f6 0%, #1a1a1a 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  margin-bottom: 20px;
  display: block;
}

.faq-header h2 {
  font-size: 36px;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 12px;
}

.faq-subtitle {
  font-size: 18px;
  color: #64748b;
  font-weight: 500;
}

.faq-container {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 50px;
}

.faq-item {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-item:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.1);
}

.faq-item.active {
  border-color: #3b82f6;
  background: linear-gradient(to bottom, #ffffff, #f8f9ff);
}

.faq-question {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  transition: all 0.3s ease;
}

.faq-question:hover {
  color: #3b82f6;
}

.faq-item.active .faq-question {
  color: #3b82f6;
}

.question-text {
  flex: 1;
  padding-right: 20px;
}

.faq-question i {
  font-size: 16px;
  color: #3b82f6;
  transition: transform 0.3s ease;
}

.faq-answer {
  overflow: hidden;
}

.answer-content {
  padding: 0 28px 24px 28px;
}

.answer-content p {
  font-size: 16px;
  line-height: 1.7;
  color: #475569;
  margin: 0;
}

/* Transition animations */
.faq-slide-enter-active,
.faq-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.faq-slide-enter-from,
.faq-slide-leave-to {
  opacity: 0;
  max-height: 0;
}

.faq-slide-enter-to,
.faq-slide-leave-from {
  opacity: 1;
  max-height: 500px;
}

.faq-footer {
  margin-top: 60px;
}

.still-have-questions {
  background: linear-gradient(135deg, #3b82f6 0%, #1a1a1a 100%);
  border-radius: 20px;
  padding: 40px;
  display: flex;
  align-items: center;
  gap: 30px;
  color: white;
  flex-wrap: wrap;
}

.still-have-questions > i {
  font-size: 48px;
  opacity: 0.9;
}

.still-have-questions > div {
  flex: 1;
  min-width: 200px;
}

.still-have-questions h3 {
  font-size: 24px;
  font-weight: 800;
  margin: 0 0 8px 0;
}

.still-have-questions p {
  font-size: 16px;
  margin: 0;
  opacity: 0.9;
}

.contact-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 14px 28px;
  background: white;
  color: #3b82f6;
  text-decoration: none;
  font-weight: 700;
  font-size: 16px;
  border-radius: 12px;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.contact-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.contact-btn i {
  font-size: 18px;
}

/* Responsive Design */
@media (max-width: 768px) {
  .faq-header h2 {
    font-size: 28px;
  }

  .faq-header i {
    font-size: 48px;
  }

  .faq-question {
    padding: 20px;
    font-size: 16px;
  }

  .answer-content {
    padding: 0 20px 20px 20px;
  }

  .still-have-questions {
    flex-direction: column;
    text-align: center;
    padding: 30px 20px;
    gap: 20px;
  }

  .still-have-questions > i {
    font-size: 36px;
  }

  .contact-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
